import {matchExpression} from '../matchExpression';
import * as Expressions from '../Expressions';
import * as Variable from '../Variable';
import * as Types from '../Types';

import {type Expression} from '@packages/systems/wfdl-ast';
import * as Qualified from '@packages/systems/wfdl-Qualified';

function indent(str: string, columns: number) {
  return ' '.repeat(columns) + str;
}

function wrap(
  before: string,
  body: Array<string>,
  after: string,
  depth: number = 0
) {
  if (body.length === 0) {
    return `${before} ${after}`;
  } else {
    return `${before}\n${list(body, depth)}\n${indent(after, depth)}`;
  }
}

function list(listOfItems: Array<string>, depth: number): string {
  if (listOfItems.length === 0) return '';
  const indentStr = ' '.repeat(depth + TABSIZE);
  let result = '';
  for (let i = 0; i < listOfItems.length; i++) {
    if (i > 0) result += '\n';
    result += indentStr + listOfItems[i] + ',';
  }
  return result;
}

/**
 * Contextual data used by the printer in order to generate formatted,
 * human-readable syntax.
 */
type PrinterInfo = {
  /** The current expression being printed */
  expr: Expression;
  /** The parent expression of `expr` (if any) */
  parent: Expression | null;
  /** The number of spaces we should currently be indented by. */
  depth: number;
};

const TABSIZE = 2;

const _print: (arg1: Expression) => (arg1: PrinterInfo) => string =
  matchExpression({
    Variable: (val) => (_: PrinterInfo) =>
      Variable.match({
        unqualified: (varName) =>
          Qualified.isValidIdentifier(varName)
            ? varName
            : `::[${JSON.stringify(varName)}]`,
        qualified: Qualified.toHumanReadableString,
      })(val),
    Function:
      (val) =>
      ({depth, expr, parent}) => {
        const paramName = Qualified.isValidIdentifier(val.param)
          ? val.param
          : `::[${JSON.stringify(val.param)}]`;
        const inner = `${paramName} => ${_print(val.body)({
          depth,
          parent: expr,
          expr: val.body,
        })}`;

        // If this function is being called, wrap it in parenthesis:
        const call = parent ? Expressions.getCall(parent) : null;
        if (call && call.fun === expr) {
          return `(${inner})`;
        }

        return inner;
      },
    Call:
      (val) =>
      ({depth, expr}) =>
        _print(val.fun)({depth, expr: val.fun, parent: expr}) +
        `(${_print(val.arg)({depth, expr: val.arg, parent: expr})})`,
    Hole: (_val) => (_: PrinterInfo) => '-',
    Number: (val) => (_: PrinterInfo) => String(val),
    // `JSON.stringify` escapes `"`, `\`, `\n`, `\r`, `\t`, `\b`, `\f`, and
    // control chars (as `\uXXXX`) — exactly the grammar the parser's lexer
    // accepts (see wfdl-parser/grammars/lexer.ts) and that
    // `JSON.parse(token.value)` reverses (see wfdl-parser/grammars/wfdl-shared.ne).
    // Needed so Text values that contain `"` or newlines (e.g. an
    // HtmlEmbed `html` field with inline SVG/HTML) round-trip cleanly.
    Text: (val) => (_: PrinterInfo) => JSON.stringify(val),
    List:
      (val) =>
      ({depth, expr}) =>
        wrap(
          '[',
          val.map((v) =>
            _print(v)({depth: depth + TABSIZE, expr: v, parent: expr})
          ),
          ']',
          depth
        ),
    Record:
      (val) =>
      ({depth, expr}) => {
        const keys = Object.keys(val);
        const items: string[] = [];
        for (const key of keys) {
          const keyStr = Qualified.isValidIdentifier(key)
            ? key
            : JSON.stringify(key);
          // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
          const valueStr = _print(val[key])({
            depth: depth + TABSIZE,
            // @ts-expect-error - TS2322 - Type 'Expression | undefined' is not assignable to type 'Expression'.
            expr: val[key],
            parent: expr,
          });
          items.push(`${keyStr}: ${valueStr}`);
        }
        return wrap('{', items, '}', depth);
      },
    Select:
      (val) =>
      ({depth, expr}) => {
        // e.g. foo.bar or foo["hello there"]
        //         ^^^^       ^^^^^^^^^^^^^^^
        const selection = Qualified.isValidIdentifier(val.prop)
          ? '.' + val.prop
          : `[${JSON.stringify(val.prop)}]`;
        return (
          _print(val.from)({depth, expr: val.from, parent: expr}) + selection
        );
      },
    Union:
      (val) =>
      ({depth, expr}) => {
        const printedTag = Qualified.isValidIdentifier(val.tag)
          ? val.tag
          : `::[${JSON.stringify(val.tag)}]`;

        const printedValue = _print(val.value)({
          depth,
          expr: val.value,
          parent: expr,
        });

        return `${printedTag} ^ ${printedValue}`;
      },
    Case:
      (val) =>
      ({depth, expr, parent}) => {
        const printedValue = _print(val.value)({
          expr: val.value,
          parent: expr,
          depth,
        });

        const printedCaseExpression = wrap(
          `case ${printedValue} {`,
          Object.keys(val.cases).map((caseName) => {
            const printedCaseName = Qualified.isValidIdentifier(caseName)
              ? caseName
              : JSON.stringify(caseName);

            // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
            const printedCaseBody = _print(val.cases[caseName])({
              // @ts-expect-error - TS2322 - Type 'Expression | undefined' is not assignable to type 'Expression'.
              expr: val.cases[caseName],
              parent: expr,
              depth: depth + TABSIZE,
            });

            return `${printedCaseName}: ${printedCaseBody}`;
          }),
          '}',
          depth
        );

        // If this case expression is being called, wrap it in parenthesis:
        const call = parent ? Expressions.getCall(parent) : null;
        if (call && call.fun === expr) {
          return `(${printedCaseExpression})`;
        }

        return printedCaseExpression;
      },
    Element:
      (val) =>
      ({depth, expr}) => {
        const dataExpr = Expressions.getElementData(val);
        const humanReadableName = Qualified.toHumanReadableString(val.type);
        const printedId = JSON.stringify(val.id);
        return `<${humanReadableName} ${printedId}> ${_print(dataExpr)({expr: dataExpr, parent: expr, depth})} </${humanReadableName}>`;
      },
    Boolean: (val) => (_: PrinterInfo) => String(val),
    Enum: (val) => (_: PrinterInfo) => {
      // Enum values with non-alphanumeric chars (spaces, commas, hyphens,
      // etc.) need to be quoted so the parser can round-trip them. Without
      // quotes the tokenizer splits on special chars and parsing fails.
      // Hyphens are excluded because a bare `-` collides with Hole syntax.
      // Purely numeric values are excluded because the tokenizer parses them
      // as number tokens rather than identifiers.
      const isSimple = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(val);
      if (isSimple) return `>${val}`;
      return `>"${val.replace(/"/g, '\\"')}"`;
    },
    Literal: (val) => (_: PrinterInfo) =>
      `literal ${Qualified.toHumanReadableString(val.name)} ( ${JSON.stringify(val.value)} )`,
    Extend:
      (val) =>
      ({depth, expr}) =>
        `{\n${indent(
          `...${_print(val.record)({
            expr: val.record,
            parent: expr,
            depth: depth + TABSIZE,
          })},`,
          depth + TABSIZE
        )}\n${indent(
          `${val.prop}: ${_print(val.value)({
            expr: val.value,
            parent: expr,
            depth,
          })},`,
          depth + TABSIZE
        )}\n${indent('}', depth)}`,
    Typed:
      (val) =>
      ({depth, expr}) =>
        wrap(
          '(',
          [
            `${_print(val.expr)({
              expr: val.expr,
              parent: expr,
              depth,
            })}: ${Types.print(val.type)}`,
          ],
          ')'
        ),
    Distinct:
      (val) =>
      ({depth, expr}) =>
        `distinct ${Qualified.toHumanReadableString(val.name)} ( ${_print(
          val.value
        )({
          expr: val.value,
          parent: expr,
          depth,
        })} )`,
  });

/**
 * Print a wfdl syntax tree as wfdl syntax
 */
export const print = (expr: Expression) =>
  _print(expr)({
    expr,
    parent: null,
    depth: 0,
  });
